import { useMemo, useState } from "react";
import { QueryCache, useQuery } from "react-query";
import { dehydrate } from "react-query/hydration";
import {
  Leaderboard,
  LeaderboardBaseProps,
} from "@ecologi/react-components/src/Leaderboard";
import { LeaderboardVariant } from "@ecologi/react-components/src/LeaderboardVariant";
import { InlineLoadingError } from "@ecologi/react-components/src/InlineLoadingError";
import { UserRankings } from "@ecologi/shared";
import { Head } from "../../components";
import {
  getLeaderboardStatsByType,
  LeaderboardPathType,
  APIError,
  forkResult,
} from "../../lib";
import { selectRankings, selectSocialImageUrl } from "./selectors";
import {
  MascotCTA,
  MascotCTAColourWays,
} from "@ecologi/react-components/src/MascotCTA";
import { useStore } from "../../store";
import {
  ReferralModal,
  ReferralModalVariants,
} from "@ecologi/react-components/src";

type LeaderboardDataProps = {
  userRankings: UserRankings[];
} & LeaderboardBaseProps;

async function prefetchData(
  variant: LeaderboardVariant,
  url: LeaderboardPathType
) {
  const queryCache = new QueryCache();
  await queryCache.prefetchQuery([variant, url], getLeaderboardStatsByType);
  return {
    dehydratedState: dehydrate(queryCache),
    layoutProps: {
      isLightHeader: true,
      isWhiteBody: false,
    },
  };
}

function LeaderboardData({ userRankings, ...props }: LeaderboardDataProps) {
  const rankingProps = useMemo(
    () => selectRankings(userRankings),
    [userRankings]
  );

  return <Leaderboard {...rankingProps} {...props} />;
}

type LeaderboardMetaProps = {
  title: string;
  tagline: string;
};

export function LeaderboardPage(
  props: LeaderboardBaseProps & LeaderboardMetaProps
) {
  const { user } = useStore();
  const [isReferralModalVisible, setIsReferralModalVisible] = useState(false);
  const getVariantMappings = (variant: string) => {
    const isLoading = typeof user === "undefined";

    if (!user && !isLoading) {
      return {
        title: "Start making a difference today",
        subtitle: "Fund the best climate solutions",
        buttons: [
          { text: "Individual", link: "/individuals" },
          {
            text: "Business",
            link: "/",
          },
        ],
      };
    }

    switch (variant) {
      case "referral":
        return {
          title: "Want to improve your position?",
          subtitle: "Get more sparkly trees",
          buttons: [
            {
              text: "Refer a friend",
              onClick: () => setIsReferralModalVisible(true),
            },
          ],
        };
      case "members":
        return {
          title: "Want to improve your position?",
          subtitle: "Fund more trees today",
          buttons: [
            {
              text: "Buy more",
              link: "/impact-shop",
            },
          ],
        };
      case "business":
        return {
          title: "Want to improve your position?",
          subtitle: "Fund more trees today",
          buttons: [
            {
              text: "Buy more",
              link: "/impact-shop",
            },
          ],
        };
      default:
        return {
          title: "Start making a difference today",
          subtitle: "Fund the best climate solutions",
          buttons: [
            { text: "Individual", link: "/individuals" },
            {
              text: "Business",
              link: "/",
            },
          ],
        };
    }
  };

  const { title, tagline, variant } = props;
  const result = useQuery<UserRankings[], APIError>(
    [props.variant, props.url],
    getLeaderboardStatsByType
  );
  const referralLink = `https://ecologi.com${user?.getProfilePath()}?direct=true&r=${
    user?.userId
  }`;

  const socialImgUrl = selectSocialImageUrl(title);

  return (
    <>
      <Head
        title={`${title} Leaderboard`}
        description={tagline}
        imageUrl={socialImgUrl}
      />
      {forkResult<UserRankings[]>(
        {
          loading: () => (
            <>
              <Leaderboard.Loading {...props} />
              <MascotCTA
                title={getVariantMappings(variant).title}
                subtitle={getVariantMappings(variant).subtitle}
                buttons={getVariantMappings(variant).buttons}
                colourWay={MascotCTAColourWays.FOREST}
              />
            </>
          ),
          data: (userRankings) => (
            <>
              <LeaderboardData userRankings={userRankings} {...props} />
              <MascotCTA
                title={getVariantMappings(variant).title}
                subtitle={getVariantMappings(variant).subtitle}
                buttons={getVariantMappings(variant).buttons}
                colourWay={MascotCTAColourWays.FOREST}
              />
            </>
          ),
          empty: () => <div>EMPTY</div>,
          error: InlineLoadingError,
        },
        result
      )}
      <ReferralModal
        variant={
          user?.isBusinessType
            ? ReferralModalVariants.B2B
            : ReferralModalVariants.CONSUMER
        }
        isOpen={isReferralModalVisible}
        referralLink={referralLink}
        onClose={() => setIsReferralModalVisible(false)}
      />
    </>
  );
}

LeaderboardPage.prefetchData = prefetchData;
